import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useModules } from '@/contexts/ModuleContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { apiClient } from '@/lib/api';
import {
  MOBILE_HEADER_ACTIONS,
  type MobileHeaderActionId,
  type MobileHeaderActionMeta,
} from '@/lib/mobileHeaderActions';

// Decide quali shortcut del catalogo possono comparire per l'utente corrente.
// La logica è la stessa per i due slot (header e bottom bar): se la feature
// non è abilitata per il tenant/utente, la voce sparisce dal selettore in
// Settings (così l'utente non può mai sceglierla) e dai renderer (così non
// viene mai disegnata anche se persiste in localStorage da una vecchia
// configurazione).
export function useMobileShortcutAvailability(): {
  isAvailable: (id: MobileHeaderActionId) => boolean;
  visibleCatalog: MobileHeaderActionMeta[];
  filterIds: (ids: MobileHeaderActionId[]) => MobileHeaderActionId[];
  loading: boolean;
} {
  const { user } = useAuth();
  const { isModuleEnabled } = useModules();
  const { hasPermission } = usePermissions();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // L'unica feature che richiede una chiamata di rete per scoprire se è
  // attiva è l'Assistente Personale (lo stesso pattern già usato dal pannello
  // "Header mobile" oggi).
  const [assistantActive, setAssistantActive] = useState<boolean | null>(null);
  useEffect(() => {
    if (!user) {
      setAssistantActive(false);
      return;
    }
    let alive = true;
    apiClient
      .get<{ id?: string; is_active?: boolean }>('/personal-assistant/my-assistant')
      .then((res) => {
        const a = (res?.data || res) as { id?: string; is_active?: boolean } | undefined;
        if (alive) setAssistantActive(!!(a && a.id && a.is_active));
      })
      .catch(() => {
        if (alive) setAssistantActive(false);
      });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  const wikiEnabled = isModuleEnabled('company_wiki') || isAdmin;
  const aiToolsEnabled = (isModuleEnabled('ai_tools') && hasPermission('ai_tools.access')) || isAdmin;
  const feedEnabled = isModuleEnabled('messenger') && hasPermission('messenger.access');
  const messengerEnabled = isModuleEnabled('messenger') && hasPermission('messenger.access');

  const isAvailable = useMemo(() => {
    return (id: MobileHeaderActionId): boolean => {
      switch (id) {
        case 'ai-assistant':
          // `null` = ancora in caricamento → mostriamo (verrà filtrata se la
          // chiamata risponde negativo). Evita flicker iniziale.
          return assistantActive !== false;
        case 'wiki':
          return wikiEnabled;
        case 'ai-tools':
          return aiToolsEnabled;
        case 'feed':
          return feedEnabled;
        case 'messaging-chat':
        case 'messaging-feed':
        case 'messaging-groups':
        case 'messaging-channels':
          return messengerEnabled;
        default:
          return true;
      }
    };
  }, [assistantActive, wikiEnabled, aiToolsEnabled, feedEnabled, messengerEnabled]);

  const visibleCatalog = useMemo(
    () => MOBILE_HEADER_ACTIONS.filter((m) => isAvailable(m.id)),
    [isAvailable],
  );

  const filterIds = useMemo(() => {
    return (ids: MobileHeaderActionId[]) => ids.filter(isAvailable);
  }, [isAvailable]);

  return {
    isAvailable,
    visibleCatalog,
    filterIds,
    loading: assistantActive === null,
  };
}
